import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { session, login, loading, error:connectionError, refresh } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword,setShowPassword]=useState(false),[error,setError]=useState('');

  if (loading) return <div className="p-8">Checking sign-in…</div>;
  if (session) return <Navigate to={session.user.role==='admin'&&session.organisation?.onboarding_required?'/setup':'/quotes'} replace />;
  if (connectionError) return <div role="alert" className="p-8">{connectionError}<button className="ml-4 underline" onClick={refresh}>Retry connection</button></div>;

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);setError('');
    try {
      const result=await login(email.trim().toLowerCase(), password);
      toast.success("Signed in");
      nav(result.user.role==='admin'&&result.organisation?.onboarding_required?'/setup':'/quotes');
    } catch (err) {
      const unavailable = !err?.response;
      const detail=err?.response?.data?.detail;
      setError(typeof detail==='string'?detail:unavailable?'SmartQuote is unavailable. Please try again shortly.':'Check your email and password, then try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--sq-page)] flex flex-col">
      <div className="w-full bg-slate-900 border-b border-slate-800 text-white px-4 py-1.5 text-[12px] uppercase text-center">
        SmartQuote V2
      </div>
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-between bg-[var(--sq-sidebar)] text-slate-200 p-12">
          <div>
            <div className="font-display text-3xl font-bold text-white">SmartQuote</div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-slate-400 mt-1">
              Laser, folding and secondary process quoting
            </div>
          </div>
          <div className="space-y-3 max-w-md">
            <h2 className="font-display text-2xl text-white leading-tight">
              Quote work from drawing through to order.
            </h2>
            
          </div>
          <div className="text-[11px] font-mono text-slate-500 space-y-0.5">
            <div>Secure online access</div>
            
          </div>
        </div>
        <div className="flex items-center justify-center p-8">
          <form onSubmit={onSubmit} className="w-full max-w-sm bg-white rounded-lg border border-slate-200 shadow-sm p-6 space-y-4">
            <div>
              <h1 className="font-display text-2xl font-semibold">Sign in</h1>
              <p className="text-xs text-slate-500 mt-1">Use your SmartQuote account.</p>
            </div>
            {error&&<div role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
            <label className="block">
              <span className="field-label">Email</span>
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-[var(--sq-accent)] font-mono text-sm"
                required
              />
            </label>
            <label className="block">
              <span className="field-label">Password</span>
              <input
                data-testid="login-password-input"
                type={showPassword?'text':'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-[var(--sq-accent)] font-mono text-sm"
                required
              />
            </label>
            <button type="button" aria-pressed={showPassword} className="text-xs underline" onClick={()=>setShowPassword(!showPassword)}>{showPassword?'Hide password':'Show password'}</button>
            <button
              data-testid="login-submit-button"
              disabled={busy}
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[var(--sq-sidebar)] text-white text-sm font-medium py-2.5 rounded hover:bg-slate-800 disabled:opacity-60"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <p className="text-xs text-slate-500">Contact your administrator for access.</p>
          </form>
        </div>
      </div>
    </div>
  );
}
